import { Add } from '@mui/icons-material';
import { RadioGroup, Stack, Tooltip } from '@mui/joy';
import { sortBy } from 'lodash/fp';
import { type FunctionComponent, type ReactElement, useEffect, useRef, useState } from 'react';

import CreatePresetDialog, { type FormType } from './CreatePresetDialog.tsx';
import type { ReportPreset, ReportSettings } from './GAgGridPresets.types.ts';
import PresetChip from './PresetChip.tsx';
import DialogButton from '../inputs/GButton/DialogButton.tsx';
import Button from '../inputs/GButton/GButton.tsx';
import { useDrawer } from '../Drawer/UseDrawer.tsx';
import { v4 } from 'uuid';
import { DrawerType } from '../Drawer/Drawer.types.ts';
import { useUnmount } from 'react-use';
// import { validatePresetColState } from './validateColId';
// import { useAuth } from 'components/management/NewAuthProvider';

interface ReportPresetsProps {
  gridColIds: string[];
  userSettings: ReportSettings;
  defaultPresets: ReportPreset[];
  currentPreset: ReportPreset;
  setCurrentPreset: (preset: ReportPreset) => void;
  onAddPreset: (presetData: FormType) => Promise<void>;
  onSavePreset: () => Promise<void>;
  onDeletePreset: (preset: ReportPreset) => Promise<void>;
  setFavourites: (values: string[]) => Promise<void>;
  onResetPreset: () => void;
  copyCurrentStateAsJson: () => void;
  loading: boolean;
}

const ReportPresets: FunctionComponent<ReportPresetsProps> = ({
  gridColIds,
  userSettings,
  defaultPresets,
  currentPreset,
  setCurrentPreset,
  onAddPreset,
  onSavePreset,
  onDeletePreset,
  setFavourites,
  loading,
  onResetPreset,
  copyCurrentStateAsJson,
}) => {
  const [seeAllDrawerOpen, setSeeAllDrawerOpen] = useState(false);
  const drawerApi = useRef<((preset: ReportPreset) => void) | null>(null);
  const openKeyRef = useRef(v4());
  // const { isAdmin } = useAuth();
  const { openDrawer, closeDrawer, drawer, openKey } = useDrawer();
  const allPresets = [...defaultPresets, ...(userSettings.presets ?? [])];
  // keep shared ref which will change when the props change
  const callbacks = useRef<{
    setCurrentPreset: (preset: ReportPreset) => void;
    setFavourites: (values: string[]) => Promise<void>;
    onDeletePreset: (preset: ReportPreset) => Promise<void>;
    copyCurrentStateAsJson: () => void;
  }>({
    setCurrentPreset: setCurrentPreset,
    setFavourites: setFavourites,
    onDeletePreset: onDeletePreset,
    copyCurrentStateAsJson: copyCurrentStateAsJson,
  });

  callbacks.current.setCurrentPreset = setCurrentPreset;
  callbacks.current.setFavourites = setFavourites;
  callbacks.current.onDeletePreset = onDeletePreset;
  callbacks.current.copyCurrentStateAsJson = copyCurrentStateAsJson;

  const favoritePresetsPlusCurrent = allPresets.filter(
    (preset) => (userSettings.favorites ?? []).includes(preset.name) || preset.name === currentPreset.name
  );
  const existingPresetNames = allPresets.map((preset) => preset.name);

  // const currentValidationErrors = validatePresetColState(currentPreset.settings.columns, gridColIds);
  // const isCurrentPresetValidOrNotAdmin = currentValidationErrors.length === 0 || !isAdmin;

  useEffect(() => {
    if (drawer === DrawerType.Presets && openKey === openKeyRef.current) {
      setSeeAllDrawerOpen(true);
    } else {
      setSeeAllDrawerOpen(false);
    }
  }, [openKey, drawer]);

  useUnmount(() => {
    if (drawer === DrawerType.Presets && openKey === openKeyRef.current) {
      closeDrawer();
    }
  });

  function isFavorite(preset: ReportPreset): boolean {
    return (userSettings.favorites ?? []).includes(preset.name);
  }

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <RadioGroup sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
          {favoritePresetsPlusCurrent.map((favoritePreset) => (
            <PresetChip
              key={favoritePreset.name}
              preset={favoritePreset}
              checked={currentPreset === favoritePreset}
              onChange={(): void => {
                setCurrentPreset(favoritePreset);
                drawerApi.current?.(favoritePreset);
              }}
            />
          ))}
          <Button
            size="sm"
            sx={{ minHeight: 'var(--Chip-minHeight)' }}
            variant={seeAllDrawerOpen ? 'solid' : 'plain'}
            onClick={(): void => {
              setSeeAllDrawerOpen(!seeAllDrawerOpen);
              if (!seeAllDrawerOpen) {
                const sortedPresets = sortBy(
                  [(preset): boolean => !isFavorite(preset), (preset): string => preset.name.toLowerCase()],
                  allPresets
                );

                openDrawer(DrawerType.Presets, openKeyRef.current, {
                  drawerKey: v4(),
                  gridColIds,
                  defaultFavorites: userSettings.favorites,
                  defaultPresets: sortedPresets,
                  defaultCurrentPreset: currentPreset,
                  callbacks: callbacks.current,
                  setApiCurrentPreset: (api: (preset: ReportPreset) => void) => {
                    drawerApi.current = api;
                  },
                });
              } else {
                closeDrawer();
              }
            }}
          >
            See all
          </Button>
        </RadioGroup>
        <Stack direction="row" alignItems="center" gap={1}>
          <Tooltip title="Return to current preset settings">
            <Button size="sm" variant="plain" onClick={onResetPreset}>
              Reset
            </Button>
          </Tooltip>
          <Button
            size="sm"
            onClick={onSavePreset}
            loading={loading}
            disabled={
              currentPreset.isDefault
              // || !isCurrentPresetValidOrNotAdmin
            }
          >
            Save
          </Button>
          <DialogButton
            width="fullWidth"
            startDecorator={<Add />}
            sx={{ whiteSpace: 'nowrap' }}
            // disabled={!isCurrentPresetValidOrNotAdmin}
            renderDialog={({ onClose }): ReactElement => (
              <CreatePresetDialog
                existingPresetNames={existingPresetNames}
                onClose={onClose}
                onAddPreset={onAddPreset}
              />
            )}
          >
            Save as
          </DialogButton>
        </Stack>
      </Stack>
    </>
  );
};

export default ReportPresets;
